import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Heading } from 'yoso';
import { AnimatedNumber } from '~/components/Animated/Number/AnimatedNumber';
import { BaseCard } from '~/components/Card';
import { useChallengeExpect } from '~/components/Challenge/Challenge.hooks';
import { useChallengeScore } from '~/components/Challenge/Score/ChallengeScore.hooks';
import { ChallengeScoreUpEffect } from '~/components/Challenge/Score/Effect/ChallengeScoreUpEffect';
import { useScoreUpProcedureState } from '~/components/Challenge/Score/Effect/ChallengeScoreUpEffect.hooks';
import { T } from '~/components/I18n';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { Link } from '~/components/Route/Link';
import { AutoSizeText } from '~/components/Text/AutoSize/AutoSizeText';
import { useCountdown } from '~/components/Util';
import { format, formatKaia } from '~/components/Util/number';
import styles from './ChallengeStatus.module.scss';

export type ChallengeStatusProps = {
  className?: string;
};

export const ChallengeStatus = ({ className }: ChallengeStatusProps) => {
  const history = useHistory();
  const {
    data: { score },
  } = useChallengeScore();

  const { procedureCount } = useScoreUpProcedureState();

  const initialScoreUpProcedureCount = useMemo(
    () => procedureCount,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const showEffect = procedureCount !== initialScoreUpProcedureCount;

  const {
    data: { myRank, rewardCurrency, rewardAmount, endAt },
    isInitialLoading,
  } = useChallengeExpect();

  const { days, hours, minutes, seconds } = useCountdown(endAt);

  const onGuideClick = useCallback(
    (e) => {
      e.preventDefault();
      history.push('/challenge/guide');
    },
    [history]
  );

  return (
    <div className={classNames(styles.root, className)}>
      <Link
        to="/challenge/leaderboard"
        data-event="click"
        data-event-target="challenge_banner_myrank">
        <BaseCard className={classNames(styles.card, styles.rankCard)} size="large">
          <div className={styles.cardBody}>
            <p className={styles.cardSubTitle}>
              <T name="citizen_home_challenge_rank_title" />
            </p>
            <Heading level={5} className={styles.cardTitle}>
              {myRank ? (
                <T
                  name="key_ordinal"
                  options={{
                    rank: <strong key={myRank}>{format(myRank)}</strong>,
                    count: myRank,
                    ordinal: true,
                  }}
                />
              ) : (
                <strong>
                  <T name="citizen_home_challenge_before" />
                </strong>
              )}
            </Heading>
            {myRank ? (
              <div className={styles.cardDescription}>
                <span key={0} className={styles.score}>
                  <AnimatedNumber value={score} />
                  <span className={styles.scoreUpEffectWrapper}>
                    {showEffect && <ChallengeScoreUpEffect key={procedureCount} />}
                  </span>
                </span>
                <T name="citizen_unit_score" options={['']} />{' '}
                <Chevron className={styles.chevron} />
              </div>
            ) : (
              <p
                className={styles.cardDescription}
                onClick={onGuideClick}
                data-event="click"
                data-event-target="challenge_banner_howtojoin">
                <T name="citizen_home_challenge_before_guide" />
                <Chevron className={styles.chevron} />
              </p>
            )}
          </div>
          <div>
            <div
              className={styles.cardButton}
              data-event="click"
              data-event-target="challenge_banner_seerank">
              <AutoSizeText className={styles.cardButtonText}>
                <T name="citizen_home_challenge_seerank" />
              </AutoSizeText>
            </div>
          </div>
        </BaseCard>
      </Link>
      <Link to="/challenge" data-event="click" data-event-target="challenge_banner_myreward">
        <BaseCard className={classNames(styles.card, styles.rewardCard)} size="large">
          <div className={styles.cardBody}>
            <p className={styles.cardSubTitle}>
              <T name="citizen_home_challenge_reward_title" />
            </p>
            <Heading level={5} className={styles.cardTitle}>
              <AutoSizeText>
                <strong>{formatKaia(rewardAmount, { zeroAsEmpty: isInitialLoading })}</strong>
                {rewardCurrency}
              </AutoSizeText>
            </Heading>
            <p className={classNames(styles.cardDescription, styles.countdown)}>
              <T
                name="citizen_home_challenge_countdown"
                options={[
                  `${days.toString().padStart(2, '0')}:${hours
                    .toString()
                    .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
                    .toString()
                    .padStart(2, '0')}`,
                ]}
              />
            </p>
          </div>
          <div>
            <div
              className={styles.cardButton}
              data-event="click"
              data-event-target="challenge_banner_rewardhistory">
              <AutoSizeText className={styles.cardButtonText}>
                <T name="citizen_home_challenge_rewardhistory_btn" pre="nowrap" />
              </AutoSizeText>
            </div>
          </div>
        </BaseCard>
      </Link>
    </div>
  );
};
